import { useState } from "react";
import { toast } from "sonner";
import { Lock, Globe } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { GlassCard } from "@/components/GlassCard";
import { useLanguage } from "@/i18n/LanguageContext";

/**
 * Stage 2.1: functional community visibility toggle.
 * checked = private (default). Turning it off makes the profile public,
 * which enables share-to-community defaults.
 */
export const PrivacyCard = ({ privacy, onToggle }) => {
  const { t } = useLanguage();
  const [updating, setUpdating] = useState(false);
  const isPrivate = privacy === "private";

  const handleChange = async (checked) => {
    setUpdating(true);
    try {
      await onToggle(checked ? "private" : "public");
      toast.success(t("profile.visibilityUpdated"));
    } catch {
      toast.error(t("profile.saveError"));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <GlassCard className="p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
            {isPrivate ? (
              <Lock size={16} className="text-cyan-300/80" />
            ) : (
              <Globe size={16} className="text-emerald-300/80" />
            )}
          </div>
          <div>
            <p className="text-sm font-medium">{t("profile.privateTitle")}</p>
            <p
              data-testid="profile-privacy-description"
              className="text-xs text-[hsl(var(--muted-foreground))]"
            >
              {isPrivate ? t("profile.privateDescOn") : t("profile.privateDescOff")}
            </p>
          </div>
        </div>
        <Switch
          data-testid="profile-private-switch"
          checked={isPrivate}
          disabled={updating}
          onCheckedChange={handleChange}
        />
      </div>
    </GlassCard>
  );
};
